import { Avatar, Box, Flex, LoadingOverlay, Text } from "@mantine/core"
import { CheckSquareIcon, MoneyIcon, ReceiptIcon, WalletIcon } from "@phosphor-icons/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { InvoiceApi } from "../../apis"
import { usePermission } from "../../components/permission"
import { PopoverText } from "../../components/popover-text/popover-text"
import { ColumnType, DataTable } from "../../components/data-table"
import { Tag } from "../../components/tags"
import RenderText from "../../components/ui/render-text"
import { StatusBadge } from "../../components/ui/status-badge"
import { IGeneral } from "../../interfaces/IGeneral"
import { IInvoiceHistoryItem } from "../../interfaces/IInvoiceHistoryItem"
import { IReference } from "../../interfaces/IReference"
import { currencyFormat } from "../../utils"
import { dateTimeFormat } from "../../utils/date"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import classes from "./InvoicePaidHistory.module.css"
import { Page } from "../../components/layout/Page";

export const InvoicePaidHistory = ({ data }: any) => {
  const params = useParams()
  const { t } = useTranslation("invoice")
  const overdueStatus = useSelector((state: { general: IGeneral }) => state.general.overdueStatus)
  const paymentMethod = useSelector((state: { general: IGeneral }) => state.general.paymentMethod)
  const invoiceHistoryTypes = useSelector((state: { general: IGeneral }) => state.general.invoiceHistoryTypes)
  const invoiceStatus = useSelector((state: { general: IGeneral }) => state.general.invoiceStatus)
  const invoicePaymentStatus = useSelector((state: { general: IGeneral }) => state.general.invoicePaymentStatus)

  const INV_PAYMENT_HISTORY = usePermission("INV_PAYMENT_HISTORY")

  const [filters] = useState({
    invoiceId: `${params?.id}`,
  })

  const columns = useHeader({
    Tag,
    invoiceStatus,
    overdueStatus,
    invoiceHistoryTypes,
    paymentMethod,
  })

  if (!data) return <Box pos="relative" mih={320}><LoadingOverlay visible /></Box>

  const invoiceStatusRef = invoiceStatus?.find((item: IReference) => item.code === data?.invoiceStatus);
  const paymentStatusRef = invoicePaymentStatus?.find((item: IReference) => item.code === data?.paymentStatus);
  const overdueStatusRef = overdueStatus?.find((item: IReference) => item.code === data?.overdueStatus);

  const summary = [
    {
      label: t("history.confirmedAmount"),
      value: currencyFormat(data?.confirmedAmount ?? 0),
      icon: CheckSquareIcon,
      accent: "var(--mantine-color-brand-6)",
    },
    {
      label: t("history.paidAmount"),
      value: currencyFormat(data?.paidAmount ?? 0),
      icon: MoneyIcon,
      accent: "var(--mantine-color-green-7)",
    },
    {
      label: t("history.remainingAmount"),
      value: currencyFormat(data?.amountToPay ?? 0),
      icon: WalletIcon,
      accent: "var(--mantine-color-brand-8)",
    },
  ];

  return (
    <Page>
      {/* Hero summary */}
      <Box className={classes.summary}>
        {summary.map((item) => {
          const Icon = item.icon;
          return (
            <Box
              key={item.label}
              className={classes.summaryCard}
              style={{ "--kpi-accent": item.accent } as React.CSSProperties}
            >
              <Box className={classes.summaryIcon}>
                <Icon size={16} weight="fill" />
              </Box>
              <Text component="span" className={classes.summaryLabel}>{item.label}</Text>
              <Text className={classes.summaryValue}>{item.value}</Text>
            </Box>
          );
        })}
      </Box>

      {/* Document overview */}
      <Box className={classes.overview}>
        <Box className={classes.overviewHeader}>
          <Box className={classes.overviewIcon}>
            <ReceiptIcon size={17} weight="fill" />
          </Box>
          <Box miw={0}>
            <Text className={classes.overviewTitle}>
              {t("history.documentOverview")}
            </Text>
            <Text className={classes.overviewSubtitle}>
              {t("history.documentOverviewSubtitle")}
            </Text>
          </Box>
        </Box>

        <Box className={classes.overviewGrid}>
          <Box className={classes.field}>
            <Text component="span" className={classes.fieldLabel}>{t("history.invoiceNumber")}</Text>
            <Text component="span" className={classes.fieldValue}>
              <Text component="span" className={classes.fieldCode}>#{data?.refCode ?? "-"}</Text>
            </Text>
          </Box>

          <Box className={classes.field}>
            <Text component="span" className={classes.fieldLabel}>{t("history.buyerBusiness")}</Text>
            <Text component="span" className={classes.fieldValue}>
              <Text component="span" className={classes.fieldCode}>#{data?.receiverBusiness?.refCode ?? "-"}</Text>
              {renderBusinessName(data?.receiverBusiness)}
            </Text>
          </Box>

          <Box className={classes.field}>
            <Text component="span" className={classes.fieldLabel}>{t("history.supplierBusiness")}</Text>
            <Text component="span" className={classes.fieldValue}>
              <Text component="span" className={classes.fieldCode}>#{data?.senderBusiness?.refCode ?? "-"}</Text>
              {renderBusinessName(data?.senderBusiness)}
            </Text>
          </Box>

          <Box className={classes.field}>
            <Text component="span" className={classes.fieldLabel}>{t("history.invoiceStatusLabel")}</Text>
            <Box>
              <StatusBadge color={invoiceStatusRef?.color} label={invoiceStatusRef?.name ?? data?.invoiceStatus} />
            </Box>
          </Box>

          <Box className={classes.field}>
            <Text component="span" className={classes.fieldLabel}>{t("history.paymentStatusLabel")}</Text>
            <Box>
              <StatusBadge color={paymentStatusRef?.color} label={t(`paymentStatus.${data?.paymentStatus}`, { defaultValue: paymentStatusRef?.name ?? data?.paymentStatus })} />
            </Box>
          </Box>

          <Box className={classes.field}>
            <Text component="span" className={classes.fieldLabel}>{t("history.overdueStatusLabel")}</Text>
            <Box>
              <StatusBadge color={overdueStatusRef?.color} label={overdueStatusRef?.name ?? data?.overdueStatus} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Transactions surface */}
      <Box className={classes.txSurface}>
        <Box className={classes.txHeader}>
          <Box className={classes.overviewIcon}>
            <ReceiptIcon size={17} weight="fill" />
          </Box>
          <Box miw={0}>
            <Text className={classes.txTitle}>{t("history.title")}</Text>
            <Text className={classes.txSubtitle}>
              {t("history.subtitle")}
            </Text>
          </Box>
        </Box>
        <Box className={classes.txBody}>
          {INV_PAYMENT_HISTORY.hasAccess ? (
            <DataTable name="InvoiceApi.history" columns={columns} filters={filters} loadData={(data) => InvoiceApi.history(data!)} />
          ) : (
            INV_PAYMENT_HISTORY.accessDenied()
          )}
        </Box>
      </Box>
    </Page>
  )
}

const useHeader = ({
  Tag,
  overdueStatus,
  invoiceHistoryTypes,
  invoiceStatus,
  paymentMethod,
}: {
  Tag: any
  overdueStatus: IReference[]
  invoiceHistoryTypes: IReference[]
  invoiceStatus: IReference[]
  paymentMethod: IReference[]
}): ColumnType<IInvoiceHistoryItem>[] => {
  const { t } = useTranslation("invoice")
  return [
    {
      title: "#",
      render: (_r, index) => <RenderText text={index + 1} />,
      width: 30,
    },
    {
      title: t("history.col.txDate"),
      render: (record) => <RenderText text={dateTimeFormat(record.createdAt)} />,
      width: 30,
    },
    {
      title: t("history.col.txRef"),
      dataIndex: "salesOrder.refCode",
      render: (record) => {
        return <RenderText text={record?.trxRefCode ?? "-"} />
      },
    },
    {
      title: t("history.col.txAmount"),
      dataIndex: "category",
      render: (record) => {
        return <RenderText text={currencyFormat(record?.trxAmount, { withSymbol: false })} isAmount />
      },
    },
    {
      title: t("history.col.txDesc"),
      dataIndex: "description",
      render: (record) => {
        // The backend stores the description as raw Mongolian. Translate by the
        // stable logType code instead (mn/en/zh/ko), falling back to the stored
        // string when a code has no key — so nothing ever regresses to blank.
        const desc = record?.logType
          ? t(`history.log.${record.logType}`, { defaultValue: record?.description || "-" })
          : record?.description || "-"
        return <PopoverText children={desc} />
      },
    },
    {
      title: t("history.col.paymentMethod"),
      dataIndex: "paymentMethod",
      render: (record) => (
        <RenderText text={paymentMethod.find((i: IReference) => i.code === record.paymentMethod)?.name ?? "-"} />
      ),
    },
    {
      title: t("history.col.paymentType"),
      dataIndex: "type",
      render: (record) => {
        return Tag(
          invoiceHistoryTypes?.find((item: IReference) => item.code === record?.type)?.color ?? "gray",
          invoiceHistoryTypes?.find((item: IReference) => item.code === record?.type)?.name ?? "-",
        )
      },
    },
    {
      title: t("history.col.paymentStatus"),
      dataIndex: "overdueStatus",
      render: (record) => {
        return Tag(
          overdueStatus?.find((item: IReference) => item.code === record?.overdueStatus)?.color ?? "gray",
          overdueStatus?.find((item: IReference) => item.code === record?.overdueStatus)?.name ?? "-",
        )
      },
    },
    {
      title: t("history.col.invoiceStatus"),
      dataIndex: "invoiceStatus",
      render: (record) => {
        return Tag(
          invoiceStatus?.find((item: IReference) => item.code === record?.invoiceStatus)?.color ?? "gray",
          invoiceStatus?.find((item: IReference) => item.code === record?.invoiceStatus)?.name ?? "-",
        )
      },
    },
    {
      title: t("history.col.paidBy"),
      render: (record) => {
        return record.actionUser ? (
          <Flex align="center" gap={10} w="max-content">
            <Avatar src={record.actionUser.avatar} size={32} />
            <Text tt={"capitalize"} size="sm">
              {renderName(record.actionUser)}
            </Text>
          </Flex>
        ) : (
          "-"
        )
      },
    },
    {
      title: t("history.col.debitAccNum"),
      dataIndex: "debitAcc?.number",
      render: (record) => <RenderText text={record?.creditAcc?.number ?? "-"} />,
    },
    {
      title: t("history.col.debitAccName"),
      dataIndex: "debitAcc?.name",
      render: (record) => <RenderText text={record?.creditAcc?.name ?? "-"} />,
    },
    {
      title: t("history.col.creditAccNum"),
      dataIndex: "creditAcc?.number",
      render: (record) => <RenderText text={record?.debitAcc?.number ?? "-"} />,
    },
    {
      title: t("history.col.creditAccName"),
      dataIndex: "creditAcc?.name",
      render: (record) => <RenderText text={record?.debitAcc?.name ?? "-"} />,
    },
  ]
}
